<script lang="ts" setup>
import UserAvatar from "@/components/UserAvatar.vue";
import {
  Image,
  Popup,
  showConfirmDialog,
  showFailToast,
  showImagePreview,
  showLoadingToast,
} from "vant";
import { useI18n } from "vue-i18n";
import { onMounted, ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import { useMateTrait } from "@/stores/mateTrait";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import i18n from "@/locales";
import { useRouter } from "vue-router";
import { sessionStorageSet } from "@/tools/sessionStorage";

const props = defineProps({
  showCreateBtn: {
    type: Boolean,
    default: true,
  },
  mateId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const showImgUploadPopup = ref(false);
const store = useMateTrait();
const router = useRouter();
const showEditTrait = ref(false);
const activeTrait = ref<MateTrait>({} as MateTrait);
const activeName = ref("");
const activeImg = ref();

const imgList = ref<MediaUploadFileItem[]>([]);

const onImgSelect = (img: MediaUploadFileItem[]) => {
  activeImg.value = {
    url: img[0].url,
    id: img[0].mediaId,
  };
};

const onClickEdit = ({ id, mode }: MateTrait) => {
  router.push({ name: "createTraitTask", query: { id, mode } });
};

const onClickEditName = (item: MateTrait) => {
  activeName.value = item.name;
  if (item.media?.url)
    activeImg.value = { url: item.media?.url, id: item.mediaId };
  else if (item.source?.url)
    activeImg.value = { url: item.source?.url, id: item.mediaId };
  else activeImg.value = null;

  showEditTrait.value = true;
  activeTrait.value = item;
};

const onClickSaveName = async () => {
  const toast = showLoadingToast({
    message: i18n.global.t("Message.updatingFeature"),
    duration: 0,
  });
  try {
    let { sourceId, prompt, style, negativePrompt, mediaId } =
      activeTrait.value;
    if (activeImg.value?.id) mediaId = activeImg.value.id;

    await store.updateTrait(activeTrait.value.id!, {
      mediaId,
      sourceId,
      prompt,
      style,
      negativePrompt,
      name: activeName.value,
    });
  } catch (e) {
    showFailToast(i18n.global.t("Message.failedUpdateFeature"));
    throw e;
  } finally {
    toast.close();
    showEditTrait.value = false;
  }
};

const onClickDelete = async (item: MateTrait) => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("Message.deleteFeature"),
      message: i18n.global.t("Message.confirmDeletion", { name: item.name }),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    // 取消删除
    return;
  }
  const toast = showLoadingToast({
    message: i18n.global.t("Message.deletingFeature"),
    duration: 0,
  });
  try {
    await store.deleteTrait(item.id!);
    sessionStorageSet("mateTraitDeleted:" + item.id, 1);
  } catch (e) {
    showFailToast(i18n.global.t("Message.failedDeleteFeature"));
    throw e;
  } finally {
    toast.close();
  }
};

const onClickClone = async (id: number) => {
  try {
    await showConfirmDialog({
      title: i18n.global.t("trait.edit.clone"),
      message: i18n.global.t("Message.confirmCloneTrait"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
  } catch {
    // 取消克隆
    return;
  }
  const toast = showLoadingToast({
    message: i18n.global.t("Message.cloningFeature"),
    duration: 0,
  });
  try {
    await store.cloneTrait(id);
  } catch (e) {
    showFailToast(i18n.global.t("Message.featureCloningFailed"));
    throw e;
  } finally {
    toast.close();
  }
};

const onClickPreviewImg = (url: string) => {
  if (!url) return;
  showImagePreview([url]);
};

// 创建特征弹窗
const showCreateTraitModal = ref(false);
const loading = ref(false);

onMounted(async () => {
  loading.value = true;
  await store.getOrFetchTraitListByMateId(props.mateId);
  loading.value = false;
});
</script>
<template>
  <div class="h-full grid grid-rows-[1fr] overflow-hidden">
    <div v-if="loading" class="flex justify-center items-center pt-16">
      <span class="loading loading-ring loading-lg" />
    </div>
    <div v-else class="overflow-auto pt-4 pb-10">
      <div class="flex justify-end pr-4" v-if="showCreateBtn">
        <button
          class="btn btn-primary btn-xs rounded"
          @click="showCreateTraitModal = true"
        >
          {{ t(`trait.new`) }}
        </button>
      </div>

      <div v-if="store.mateIdTraitListMap[mateId]?.length === 0">
        <div class="text-center text-base-content/50 text-sm py-4">
          {{ $t(`painting.people.noFeatureInfo`) }}
        </div>
        <div class="text-center text-base-content/50 text-sm py-4">
          {{ $t(`painting.people.noFeatureInfoDesc`) }}
        </div>
      </div>
      <div
        v-for="item in store.mateIdTraitListMap[mateId] || []"
        :key="item.id"
        class="grid grid-cols-[auto_1fr] gap-4 p-3"
      >
        <div class="w-[100px] flex flex-col items-center">
          <user-avatar
            :avatar-url="item.media?.url || item.source?.url"
            class="w-full mb-2.5"
            @click="onClickPreviewImg(item.media?.url || '')"
          />
          <div
            class="flex gap-2 max-w-full overflow-hidden"
            @click="onClickEditName(item)"
          >
            <p class="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
              {{ item.name }}
            </p>
            <i class="w-[20px] iconify mingcute--edit-3-line"></i>
          </div>
        </div>
        <div>
          <p class="text-xs mb-2.5 h-[130px] overflow-hidden">
            {{ item.prompt }}
          </p>
          <div class="flex justify-end flex-wrap gap-2">
            <button
              v-if="item?.mode !== 7"
              class="btn btn-xs rounded"
              @click="onClickClone(item.id!)"
            >
              <i class="iconify mingcute--copy-2-line"></i>
              {{ t(`trait.feature.clone`) }}
            </button>
            <button
              class="btn btn-xs rounded"
              @click="
                $router.push(`/generation/trait/portrait-list?id=${item.id}`)
              "
            >
              {{ $t("Texts.verticalPainting") }}
            </button>
            <button
              class="btn btn-xs rounded"
              @click="
                $router.push({
                  name: 'animationList',
                  query: { traitId: item.id },
                })
              "
            >
              {{ $t("Texts.animation") }}
            </button>
            <button
              v-if="item?.mode !== 7"
              class="btn btn-xs rounded"
              @click="onClickEdit(item)"
            >
              <i class="iconify mingcute--edit-3-line" />
            </button>
            <button
              class="btn btn-xs rounded px-3 inline-block"
              @click="onClickDelete(item)"
            >
              <i class="iconify mingcute--delete-2-line"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <media-select-popup
      v-model:media-list="imgList"
      v-model:show="showImgUploadPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="7"
      enable-use-avatar
      media-type="img"
      replace
      @on-media-selected="onImgSelect"
    />

    <popup
      v-model:show="showEditTrait"
      class="grid grid-rows-[1fr] py-10 px-4"
      position="bottom"
    >
      <div class="flex flex-col gap-4">
        <div class="text-base text-center font-bold">
          {{ activeTrait.name }}
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <span>{{ $t("trait.traitName") }}</span>
            <input v-model="activeName" class="input input-bordered" />
          </div>

          <div class="flex flex-col gap-2 mt-2">
            <span>{{ $t("trait.traitPreview") }}</span>
            <div class="flex justify-center" @click="showImgUploadPopup = true">
              <Image
                v-if="activeImg"
                :src="activeImg?.url"
                class="w-40 rounded overflow-hidden min-h-[213px]"
                fit="cover"
                crossorigin="anonymous"
              />
              <i
                v-else
                class="iconfont icon-a- input input-bordered w-40 text-center leading-[213px] h-[213px]"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center gap-2 mt-4">
        <button
          class="btn btn-sm btn-primary w-[200px]"
          @click="onClickSaveName"
        >
          <i class="iconfont icon-user-edit"></i>
          {{ $t("Buttons.save") }}
        </button>
        <button class="btn btn-sm w-[200px]" @click="showEditTrait = false">
          {{ $t("Buttons.cancel") }}
        </button>
      </div>
    </popup>

    <CreateTraitPresetModal v-model:show="showCreateTraitModal" />
  </div>
</template>
